import { computed, onScopeDispose, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useForceUpdateStore } from '@acs/components-library/src/stores/forceUpdateStore';
import { captureSentryException } from '@acs/components-library/src/sentry.js';
import { version as appVersion } from '../../package.json';

// When the server requires a newer app version, the only recovery on web is to
// reload and hope to pick up a freshly deployed bundle. If the required version
// is NOT actually deployed (or the server is misconfigured), reloading fetches
// the same bundle and the server keeps demanding an update — an infinite reload
// loop. This guard bounds the reloads with a persisted counter and, once the
// budget is spent, stops and surfaces a dialog instead of looping forever.

// Persisted across reloads (in-memory state is wiped on reload).
const ATTEMPTS_STORAGE_KEY = 'forceUpdate.reloadAttempts';
const MAX_RELOAD_ATTEMPTS = 3;
// Small delay before reloading so we never hammer in a tight loop (throttle).
const RELOAD_DELAY_MS = 1500;
// After giving up, keep the retry button disabled for this cool-down so the user
// can't hammer it straight back into the same failure before a new build could
// deploy.
const RETRY_ENABLE_DELAY_SECONDS = 20;

function storageAvailable(): boolean {
  try {
    const probe = '__forceUpdateProbe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function readAttempts(): number {
  try {
    const raw = window.localStorage.getItem(ATTEMPTS_STORAGE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeAttempts(value: number): void {
  try {
    window.localStorage.setItem(ATTEMPTS_STORAGE_KEY, String(value));
  } catch {
    /* localStorage unavailable (private mode, quota) — degrade gracefully */
  }
}

function clearAttempts(): void {
  try {
    window.localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// Report a give-up condition both to the console (visible when inspecting a
// user's session) and to Sentry (so we actually get alerted in production —
// captureSentryException only logs to the console in development).
function reportGuardFailure(message: string, context: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.error(`[forceUpdate] ${message}`, context);
  // Each caller's message is self-complete (carries version + attempt details),
  // so the Sentry error alone is enough to diagnose.
  captureSentryException(new Error(`[forceUpdate] ${message}`));
}

export type ForceUpdateGuard = {
  /** True once reloads are exhausted and the user-facing dialog should show. */
  updateFailedDialogOpen: Ref<boolean>;
  /** False during the post-failure cool-down; true once retry is allowed. */
  retryEnabled: ComputedRef<boolean>;
  /** Seconds remaining in the cool-down (0 once retry is enabled). */
  retrySecondsLeft: Readonly<Ref<number>>;
  /** Manual escape hatch: reset the streak and reload once more. */
  retry: () => void;
};

export function useForceUpdateGuard(): ForceUpdateGuard {
  const store = useForceUpdateStore();
  const { forceUpdateRequired, minSemVer, updateCheckResolved } = storeToRefs(store);

  const updateFailedDialogOpen = ref(false);

  // Retry cool-down: once the dialog opens, keep the button disabled for
  // RETRY_ENABLE_DELAY_SECONDS, counting down so the user sees why it's disabled.
  const retrySecondsLeft = ref(0);
  const retryEnabled = computed<boolean>(() => retrySecondsLeft.value <= 0);
  let countdownTimer: ReturnType<typeof setInterval> | null = null;

  function openFailureDialog(): void {
    updateFailedDialogOpen.value = true;
    if (countdownTimer !== null) return; // cool-down already running
    retrySecondsLeft.value = RETRY_ENABLE_DELAY_SECONDS;
    countdownTimer = setInterval(() => {
      retrySecondsLeft.value = Math.max(0, retrySecondsLeft.value - 1);
      if (retrySecondsLeft.value === 0 && countdownTimer !== null) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    }, 1000);
  }

  function evaluate(): void {
    // Ignore the pre-resolution state: forceUpdateRequired is false while the
    // query is still loading, and resetting the counter there would recreate
    // the reload loop after every boot.
    if (!updateCheckResolved.value) return;

    if (!forceUpdateRequired.value) {
      // Server no longer requires an update (a newer bundle is live, or it
      // backed off) — clear the streak so a future requirement gets a fresh
      // reload budget.
      clearAttempts();
      updateFailedDialogOpen.value = false;
      return;
    }

    if (!storageAvailable()) {
      // We cannot persist a reload counter across reloads, so we cannot bound
      // the loop — surface the dialog instead of risking an infinite reload.
      reportGuardFailure(
        `localStorage is unavailable; cannot bound the reload loop — showing the update dialog instead. `
        + `App "${appVersion}", server required minimum "${minSemVer.value ?? 'unknown'}".`,
        { reason: 'localstorage-unavailable', appVersion, requiredMinSemVer: minSemVer.value ?? null },
      );
      openFailureDialog();
      return;
    }

    const attempts = readAttempts();

    if (attempts >= MAX_RELOAD_ATTEMPTS) {
      // Reloaded MAX_RELOAD_ATTEMPTS times and the server STILL demands a newer
      // version: reloading the same deployed bundle cannot fix this (the newer
      // build is likely not deployed yet, or the server is misconfigured). Stop
      // the loop and surface an ambivalent dialog; report the specifics for us.
      reportGuardFailure(
        `aborting reload loop after ${attempts} attempts: app "${appVersion}" is still below the `
        + `server's required minimum "${minSemVer.value ?? 'unknown'}" — a newer build is likely not deployed yet.`,
        { reason: 'max-reload-attempts', appVersion, requiredMinSemVer: minSemVer.value ?? null, attempts },
      );
      openFailureDialog();
      return;
    }

    // Still within budget — count this reload and try to pick up a newer bundle.
    writeAttempts(attempts + 1);
    window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
  }

  function retry(): void {
    clearAttempts();
    window.location.reload();
  }

  // Fire on boot (immediate) and on every change to the check's outcome.
  watch([forceUpdateRequired, updateCheckResolved], evaluate, { immediate: true });

  onScopeDispose(() => {
    if (countdownTimer !== null) clearInterval(countdownTimer);
  });

  return { updateFailedDialogOpen, retryEnabled, retrySecondsLeft, retry };
}
